import { useRecordContext, type RaRecord } from 'react-admin';
import { Alert, Box, Chip, CircularProgress, Stack, Typography } from '@mui/material';
import { SectionCard } from '../../shared/Panel';
import { useFeature } from '../featureFlags/FeatureGate';
import {
  FEATURE_FUEL_SURCHARGES,
  rangeText,
  useSurchargeInfo,
  type ApplicableSurcharge,
} from './fuelSurchargesApi';

/**
 * „Korekta paliwowa” na karcie zlecenia i prezlecenia (makieta back office
 * v2, fala B, B4; BR-ADM-04; przełącznik `fuelSurcharges`, 21.10.2026).
 *
 * Jedno zdanie z API: rodzaj, okres, moment naliczenia (wg daty załadunku
 * albo rozładunku) i wartość na ten dzień — albo „brak — czeka na wartości
 * od CS”, nigdy zero. **Tylko informacja** (decyzja D8): marża i koszt na
 * karcie liczą się bez korekty. Sekcję widzi każdy, kto widzi kartę;
 * kartotekę zmienia przyjęcie zleceń na ekranie „Korekty paliwowe”.
 * Wyłączony przełącznik — sekcji nie ma.
 */

const SurchargeLine = ({ s }: { s: ApplicableSurcharge }) => (
  <Box data-testid="fuel-surcharge-line" sx={{ mb: 1 }}>
    <Typography variant="body2" data-testid="fuel-surcharge-text" sx={{ wordBreak: 'break-word' }}>
      {s.text}
    </Typography>
    <Stack direction="row" spacing={0.5} useFlexGap sx={{ flexWrap: 'wrap', mt: 0.5, alignItems: 'center' }}>
      {s.value ? (
        <Chip size="small" color="info" label={`wartość: ${s.value.text}`} data-testid="fuel-surcharge-value" />
      ) : (
        <Chip size="small" variant="outlined" label="wartość: brak" data-testid="fuel-surcharge-value-missing" />
      )}
      <Typography variant="caption" color="text.secondary">
        przypisanie kontrahenta {rangeText(s.assignmentFrom, s.assignmentTo)}
        {s.value ? ` · źródło wartości: ${s.value.source}` : ''}
      </Typography>
    </Stack>
  </Box>
);

export const FuelSurchargeSection = ({
  kind,
  id,
  refreshKey,
}: {
  kind: 'order' | 'pre-order';
  id: number | string | undefined;
  /** Zmiana wczytuje korektę ponownie (okno kontroli po zmianie kontrahenta). */
  refreshKey?: unknown;
}) => {
  const on = useFeature(FEATURE_FUEL_SURCHARGES);
  const info = useSurchargeInfo(kind, id, on, refreshKey);
  if (!on || id === undefined) return null;
  return (
    <SectionCard title="Korekta paliwowa">
      <Box data-testid="fuel-surcharge-section">
        {info === undefined ? (
          <CircularProgress size={20} />
        ) : info === null ? (
          <Typography variant="body2" color="text.secondary">
            Nie udało się odczytać korekty paliwowej — odśwież kartę.
          </Typography>
        ) : (
          <>
            {info.surcharges.length === 0 ? (
              <Typography variant="body2" data-testid="fuel-surcharge-none">
                {info.note}
              </Typography>
            ) : (
              info.surcharges.map((s) => <SurchargeLine key={s.assignmentId} s={s} />)
            )}
            {info.surcharges.length > 1 && (
              <Alert severity="warning" sx={{ mb: 1 }}>
                Zlecenie wypada na styku dwóch przypisań kontrahenta — obowiązują obie korekty. Sprawdź
                przypisanie w kartotece korekt.
              </Alert>
            )}
            {info.contractor ? (
              <Typography variant="caption" color="text.secondary" component="p" data-testid="fuel-surcharge-contractor">
                Kontrahent: {info.contractor.legalName}
                {info.contractorSource ? ` — ${info.contractorSource}` : ''}
              </Typography>
            ) : null}
            <Typography variant="caption" color="text.secondary" component="p" data-testid="fuel-surcharge-margin-note">
              {info.marginNote}
            </Typography>
          </>
        )}
      </Box>
    </SectionCard>
  );
};

/** Karta zlecenia (wiersz banku). */
export const OrderFuelSurcharge = () => {
  const r = useRecordContext<RaRecord>();
  return <FuelSurchargeSection kind="order" id={r?.id} />;
};

/**
 * Karta prezlecenia — kontrahent i daty z wiersza banku, a bez niego daty
 * prezlecenia i kontrahent wybrany z kartoteki na prezleceniu
 * (`preOrderContractor`; integracja fali B, 21.10.2026).
 */
export const PreOrderFuelSurcharge = () => {
  const r = useRecordContext<RaRecord>();
  return <FuelSurchargeSection kind="pre-order" id={r?.id} />;
};
